/**
 * Shared types for the Recruiting Demo API Gateway.
 *
 * These schemas match the exact shape returned by the applicant-reviewer
 * Lambda endpoints.  Source-related fields intentionally use snake_case
 * to mirror the API Gateway response format — do NOT normalise to camelCase.
 */
import { z } from "zod";
// ===== CAMPAIGN SCHEMAS =====
export const CampaignGoalTypeSchema = z.enum(["date", "hires", "budget", "perpetual"]);
export const CampaignStatusSchema = z.enum(["active", "draft", "suspended"]);
export const HiringCampaignSchema = z.object({
    campaignId: z.uuid(),
    name: z.string(),
    status: CampaignStatusSchema,
    role: z.string(),
    location: z.string(),
    companyId: z.uuid(),
    applicants: z.number(),
    pipelineCount: z.number(),
    hires: z.number(),
    spend: z.number(),
    budget: z.number().optional(),
    goalType: CampaignGoalTypeSchema,
    goalValue: z.number().optional(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const CreateCampaignInputSchema = z.object({
    name: z.string().max(255),
    role: z.string().max(255),
    location: z.string().max(255),
    goalType: CampaignGoalTypeSchema,
    budget: z.number().optional(),
    goalValue: z.number().optional(),
});
export const UpdateCampaignInputSchema = z.object({
    name: z.string().max(255),
    role: z.string().max(255),
    location: z.string().max(255),
    status: CampaignStatusSchema,
    budget: z.number(),
    goalType: CampaignGoalTypeSchema,
    goalValue: z.number(),
}).partial();
export const ListCampaignsResponseSchema = z.object({
    campaigns: z.array(HiringCampaignSchema),
    count: z.number(),
});
// ===== SOURCE SCHEMAS (snake_case fields match API Gateway) =====
export const CampaignSpendModelSchema = z.enum(["cpa", "cpc", "cpm", "daily_budget", "referral", "organic"]);
export const RecruitmentSourceSchema = z.object({
    sourceId: z.uuid(),
    campaignId: z.uuid(),
    companyId: z.uuid(),
    name: z.string(),
    active: z.boolean(),
    spend_model: CampaignSpendModelSchema,
    color: z.string().optional(),
    type: z.string().optional(),
    cpa_bid: z.number().nullable().optional(),
    cpc: z.number().nullable().optional(),
    cpm: z.number().nullable().optional(),
    daily_budget: z.number().nullable().optional(),
    referral_bonus_per_hire: z.number().nullable().optional(),
    apps_override: z.number().nullable().optional(),
    organic_per_day: z.number().nullable().optional(),
    daily_cap_apps: z.number().nullable().optional(),
    diminishing_exponent: z.number().nullable().optional(),
    saturation_rate: z.number().nullable().optional(),
    funnel_app_to_interview: z.number().nullable().optional(),
    funnel_interview_to_offer: z.number().nullable().optional(),
    funnel_offer_to_hire: z.number().nullable().optional(),
    funnel_offer_to_background: z.number().nullable().optional(),
    funnel_background_to_hire: z.number().nullable().optional(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const CreateSourceInputSchema = z.object({
    name: z.string(),
    active: z.boolean().optional(),
    spend_model: CampaignSpendModelSchema,
    color: z.string().optional(),
    type: z.string().optional(),
    cpa_bid: z.number().nullable().optional(),
    cpc: z.number().nullable().optional(),
    cpm: z.number().nullable().optional(),
    daily_budget: z.number().nullable().optional(),
    referral_bonus_per_hire: z.number().nullable().optional(),
    apps_override: z.number().nullable().optional(),
    organic_per_day: z.number().nullable().optional(),
    daily_cap_apps: z.number().nullable().optional(),
    diminishing_exponent: z.number().nullable().optional(),
    saturation_rate: z.number().nullable().optional(),
    funnel_app_to_interview: z.number().nullable().optional(),
    funnel_interview_to_offer: z.number().nullable().optional(),
    funnel_offer_to_hire: z.number().nullable().optional(),
    funnel_offer_to_background: z.number().nullable().optional(),
    funnel_background_to_hire: z.number().nullable().optional(),
});
export const UpdateSourceInputSchema = CreateSourceInputSchema.partial();
export const ListSourcesResponseSchema = z.object({
    sources: z.array(RecruitmentSourceSchema),
    count: z.number(),
});
export const DeleteSourceResponseSchema = z.object({
    deleted: z.literal(true),
    sourceId: z.uuid(),
});
// ===== DASHBOARD SCHEMAS =====
export const DashboardResponseSchema = z.object({
    kpis: z.object({
        avgReviewSpeed: z.number(),
        interviewRate: z.number(),
        ninetyDayRetention: z.number(),
    }),
    pipeline: z.object({
        submitted: z.number(),
        reviewing: z.number(),
        interview: z.number(),
        offered: z.number(),
        background_check: z.number(),
        hired: z.number(),
        benched: z.number(),
        rejected: z.number(),
    }),
    total: z.number(),
});
// ===== JOB SCHEMAS =====
export const RecruitingJobStatusSchema = z.enum(["draft", "active", "closed"]);
export const CompensationSchema = z.object({
    min: z.number().optional(),
    max: z.number().optional(),
    type: z.enum(["hourly", "salary", "range", "hourly_plus_tips"]),
});
export const QuestionSchema = z.object({
    type: z.enum(["text", "multiple_choice", "yes_no", "video"]),
    prompt: z.string(),
    options: z.array(z.string()).optional(),
    maxDuration: z.number().optional(),
});
export const JobPostingSchema = z.object({
    jobId: z.uuid(),
    title: z.string(),
    status: RecruitingJobStatusSchema,
    roleName: z.string().optional(),
    roleId: z.uuid().optional(),
    locationName: z.string().optional(),
    locationAddress: z.string().optional(),
    locationId: z.uuid().optional(),
    companyName: z.string(),
    companyLogo: z.string().optional(),
    companyId: z.uuid(),
    compensation: CompensationSchema.optional(),
    description: z.string().optional(),
    requirements: z.string().optional(),
    schedule: z.string().optional(),
    questions: z.array(QuestionSchema).optional(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    createdBy: z.uuid().optional(),
});
export const CreateJobInputSchema = z.object({
    title: z.string().max(255),
    companyName: z.string().max(255),
    status: RecruitingJobStatusSchema.optional(),
    roleName: z.string().max(255).optional(),
    roleId: z.uuid().optional(),
    locationName: z.string().max(255).optional(),
    locationAddress: z.string().max(500).optional(),
    locationId: z.uuid().optional(),
    companyLogo: z.string().max(2000).optional(),
    compensation: CompensationSchema.optional(),
    description: z.string().max(10000).optional(),
    requirements: z.string().max(10000).optional(),
    schedule: z.string().max(2000).optional(),
    questions: z.array(QuestionSchema).optional(),
});
export const ListJobsResponseSchema = z.object({
    jobs: z.array(JobPostingSchema),
    count: z.number(),
});
// ===== PIPELINE / APPLICATION SCHEMAS =====
export const ApplicationStatusSchema = z.enum([
    "draft",
    "submitted",
    "reviewing",
    "interview",
    "offered",
    "background_check",
    "hired",
    "benched",
    "rejected",
]);
export const ManagerTransitionStatusSchema = z.enum([
    "reviewing",
    "interview",
    "offered",
    "background_check",
    "hired",
    "rejected",
    "benched",
]);
export const ApplicationSummarySchema = z.object({
    applicationId: z.uuid(),
    candidateId: z.uuid(),
    jobPostingId: z.uuid(),
    status: ApplicationStatusSchema,
    companyId: z.uuid(),
    submittedAt: z.iso.datetime().optional(),
    updatedAt: z.iso.datetime(),
    statusChangedBy: z.uuid().optional(),
    statusChangeReason: z.string().optional(),
    source: z.string().optional(),
});
export const PipelineGroupedResponseSchema = z.object({
    pipeline: z.record(ApplicationStatusSchema, z.array(ApplicationSummarySchema)),
    counts: z.record(ApplicationStatusSchema, z.number()),
    total: z.number(),
});
export const PipelinePaginatedResponseSchema = z.object({
    applications: z.array(ApplicationSummarySchema),
    count: z.number(),
    nextKey: z.string().optional(),
});
export const UpdateStatusInputSchema = z.object({
    status: ManagerTransitionStatusSchema,
    reason: z.string().optional(),
});
// ===== INTERVIEW SCHEMAS =====
export const InterviewTypeSchema = z.enum(["In-Person", "Virtual"]);
export const InterviewStatusSchema = z.enum([
    "pending_confirmation",
    "confirmed",
    "completed",
    "canceled",
]);
export const InterviewSchema = z.object({
    interviewId: z.uuid(),
    applicationId: z.uuid(),
    candidateId: z.uuid(),
    jobPostingId: z.uuid(),
    companyId: z.uuid(),
    candidateName: z.string(),
    role: z.string(),
    type: InterviewTypeSchema,
    status: InterviewStatusSchema,
    duration: z.number(),
    location: z.string().optional(),
    availableSlots: z.array(z.iso.datetime()),
    selectedSlot: z.iso.datetime().optional(),
    scheduledAt: z.iso.datetime().optional(),
    feedbackNotes: z.string().optional(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const CreateInterviewInputSchema = z.object({
    applicationId: z.uuid(),
    candidateId: z.uuid(),
    jobPostingId: z.uuid(),
    candidateName: z.string(),
    role: z.string(),
    type: InterviewTypeSchema,
    duration: z.number(),
    location: z.string().optional(),
    availableSlots: z.array(z.iso.datetime()),
});
export const ListInterviewsResponseSchema = z.object({
    upcoming: z.array(InterviewSchema),
    pastPendingFeedback: z.array(InterviewSchema),
});
// ===== MANAGER APPLICATIONS SCHEMAS =====
export const ListManagerAppsQuerySchema = z.object({
    jobPostingId: z.uuid().optional(),
    status: ApplicationStatusSchema.optional(),
    campaignId: z.uuid().optional(),
    limit: z.number().optional(),
});
export const ListApplicationsResponseSchema = z.object({
    applications: z.array(ApplicationSummarySchema),
});
// ===== MANAGER AVAILABILITY SCHEMAS =====
/** Half-hour time slots for a single day (e.g. ["09:00","09:30","10:00"]) */
export const RecruitingHalfHourSlotSchema = z.string().regex(/^\d{2}:\d{2}$/).refine((val) => {
    const [h, m] = val.split(":").map(Number);
    return h >= 0 && h <= 23 && (m === 0 || m === 30);
}, { message: "Must be a valid half-hour slot (HH:00 or HH:30, 00-23)" });
export const RecruitingDayOfWeekSchema = z.enum([
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
]);
export const ManagerAvailabilitySchema = z.record(RecruitingDayOfWeekSchema, z.array(RecruitingHalfHourSlotSchema));
// ===== OFFER / HIRE SCHEMAS =====
export const OfferInputSchema = z.object({
    offerDetails: z.string().max(5000).optional(),
});
export const HireResponseSchema = z.object({
    applicationId: z.uuid(),
    status: z.literal("hired"),
    hiredAt: z.iso.datetime(),
});
// ===== CANDIDATE PROFILE SCHEMAS =====
export const RecruitingWorkHistoryEntrySchema = z.object({
    employer: z.string(),
    title: z.string(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
});
export const CandidateProfileSchema = z.object({
    candidateId: z.uuid(),
    name: z.string(),
    phone: z.string().optional(),
    photo: z.string().optional(),
    address: z.string().optional(),
    availability: ManagerAvailabilitySchema.optional(),
    workHistory: z.array(RecruitingWorkHistoryEntrySchema).optional(),
    profileComplete: z.boolean(),
});
// ===== MANAGER MEDIA SCHEMAS =====
export const RecruitingMediaTypeSchema = z.enum(["video", "image", "document", "video_answer", "id_front", "id_back", "photo"]);
export const RecruitingMediaStatusSchema = z.enum(["pending", "processing", "ready", "failed"]);
export const ManagerMediaSchema = z.object({
    mediaId: z.uuid(),
    type: RecruitingMediaTypeSchema,
    status: RecruitingMediaStatusSchema,
    candidateId: z.uuid(),
    applicationId: z.uuid().optional(),
    downloadUrl: z.string().optional(),
    createdAt: z.iso.datetime(),
});
export const ListManagerMediaResponseSchema = z.object({
    media: z.array(ManagerMediaSchema),
});
// ===== CANDIDATE ANSWER SCHEMAS =====
const CandidateTextAnswerSchema = z.object({
    questionId: z.uuid(),
    questionText: z.string(),
    type: z.literal("text"),
    answer: z.string().nullable(),
});
const CandidateMultipleChoiceAnswerSchema = z.object({
    questionId: z.uuid(),
    questionText: z.string(),
    type: z.literal("multiple_choice"),
    answer: z.array(z.string()).nullable(),
    options: z.array(z.string()),
});
const CandidateYesNoAnswerSchema = z.object({
    questionId: z.uuid(),
    questionText: z.string(),
    type: z.literal("yes_no"),
    answer: z.boolean().nullable(),
});
const CandidateVideoAnswerSchema = z.object({
    questionId: z.uuid(),
    questionText: z.string(),
    type: z.literal("video"),
    answer: z.null(),
    mediaId: z.uuid().nullable(),
});
export const CandidateAnswerSchema = z.discriminatedUnion("type", [
    CandidateTextAnswerSchema,
    CandidateMultipleChoiceAnswerSchema,
    CandidateYesNoAnswerSchema,
    CandidateVideoAnswerSchema,
]);
// ===== UPDATE INTERVIEW SCHEMA =====
export const UpdateInterviewInputSchema = z.object({
    status: InterviewStatusSchema.optional(),
    selectedSlot: z.iso.datetime().optional(),
    scheduledAt: z.iso.datetime().optional(),
    feedbackNotes: z.string().max(2000).optional(),
    location: z.string().max(2000).optional(),
    duration: z.number().optional(),
    availableSlots: z.array(z.iso.datetime()).optional(),
});
// ===== PLANNER / STRATEGY SCHEMAS =====
export const SpendStrategySchema = z.enum(["static", "pulsed", "adaptive"]);
export const PulseParamsSchema = z.object({
    peakMultiplier: z.number(),
    peakDays: z.array(z.number()), // 0=Mon..6=Sun
    troughMultiplier: z.number(),
});
export const AdaptiveParamsSchema = z.object({
    targetLow: z.number(),
    targetHigh: z.number(),
    adjustRate: z.number(),
});
export const PlannerInputsSchema = z.object({
    strategy: SpendStrategySchema,
    pulseParams: PulseParamsSchema.optional(),
    adaptiveParams: AdaptiveParamsSchema.optional(),
});
